import { Globe, Loader2, Mic, Send, Square } from "lucide-react"
import { useState } from "react"

import { AudioVisualizer } from "@/components/conversation/audio-visualizer"
import { ThaiFlagMap } from "@/components/icons/thai-flag-map"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"
import { cn } from "@/lib/utils"

const MAX_CHARS = 75

type Language = "EN" | "TH"

interface InputBarProps {
  onSend?: (message: string, language: Language) => void
  onAudioRecorded?: (blob: Blob) => void
  isProcessingAudio?: boolean
}

export default function InputBar({
  onSend,
  onAudioRecorded,
  isProcessingAudio = false,
}: InputBarProps) {
  const [language, setLanguage] = useState<Language>("EN")
  const [message, setMessage] = useState("")

  const {
    isRecording,
    duration,
    error,
    start,
    stop,
    cancel,
    getFrequencyData,
  } = useAudioRecorder()

  // Detect if text contains Thai characters
  const containsThaiCharacters = (text: string): boolean => {
    return /[\u0E00-\u0E7F]/.test(text)
  }

  const toggleLanguage = () => {
    const newLang = language === "EN" ? "TH" : "EN"
    setLanguage(newLang)
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value
    // Allow typing but enforce max length
    if (newMessage.length > MAX_CHARS) return
    setMessage(newMessage)

    // Auto-detect language based on input
    if (newMessage.trim().length > 0) {
      const detectedLanguage = containsThaiCharacters(newMessage) ? "TH" : "EN"
      if (detectedLanguage !== language) {
        setLanguage(detectedLanguage)
      }
    }
  }

  const handleSend = () => {
    if (message.trim().length === 0) return

    // Call the onSend callback if provided
    if (onSend) {
      onSend(message, language)
    }

    setMessage("") // Clear input after send
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleMicClick = () => {
    if (isRecording) {
      // Stop recording and send audio
      stop()
        .then((blob) => {
          if (blob && onAudioRecorded) {
            onAudioRecorded(blob)
          }
        })
        .catch((error) => {
          console.error("Failed to stop recording:", error)
        })
    } else {
      // Start recording
      void start()
    }
  }

  const handleCancelRecording = () => {
    cancel()
  }

  // Character ring calculations
  const charCount = message.length
  const percentage = (charCount / MAX_CHARS) * 100
  const remaining = MAX_CHARS - charCount

  // Ring color based on percentage
  const getRingColor = () => {
    if (percentage >= 100) return "text-red-500"
    if (percentage >= 80) return "text-yellow-500"
    return "text-emerald-500"
  }

  // SVG circle calculations (radius=10, circumference=2πr≈62.83)
  const circumference = 62.83
  const strokeDashoffset = circumference - (percentage / 100) * circumference

  const isDisabled = isRecording || isProcessingAudio

  return (
    <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 shadow-lg dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl space-y-2">
        {/* Audio Visualizer - shows when recording */}
        {isRecording && (
          <AudioVisualizer
            duration={duration}
            getFrequencyData={getFrequencyData}
            onCancel={handleCancelRecording}
          />
        )}

        {/* Processing indicator */}
        {isProcessingAudio && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-600 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
            <Loader2 className="size-4 animate-spin" />
            <span>Transcribing audio...</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/30 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          {/* Language Toggle */}
          <Button
            variant="outline"
            size="default"
            onClick={toggleLanguage}
            className="shrink-0 gap-2"
            aria-label={`Current language: ${language}`}
            disabled={isDisabled}
          >
            {language === "EN" ? (
              <Globe className="size-5" />
            ) : (
              <ThaiFlagMap className="size-6" />
            )}
            <span className="text-sm font-medium">{language}</span>
          </Button>

          {/* Text Input with Character Ring Overlay */}
          <div className="relative flex-1">
            <Textarea
              value={message}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              placeholder={
                language === "EN" ? "Type message..." : "พิมพ์ข้อความ..."
              }
              lang={language === "EN" ? "en" : "th"}
              className="max-h-[120px] min-h-[40px] w-full resize-none py-2 pr-10"
              aria-label="Message input"
              rows={1}
              disabled={isDisabled}
            />

            {/* Character Ring - Inside textarea */}
            {charCount > 0 && (
              <div className="absolute right-2 bottom-2 flex size-6 items-center justify-center">
                <svg className="size-6 -rotate-90" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-slate-200 dark:text-slate-700"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className={cn(
                      "transition-all duration-150",
                      getRingColor(),
                    )}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                  />
                </svg>
                {remaining <= 20 && (
                  <span
                    className={cn(
                      "absolute text-[8px] font-medium",
                      remaining <= 0
                        ? "text-red-500"
                        : "text-slate-600 dark:text-slate-400",
                    )}
                  >
                    {remaining}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Contextual Mic/Send Button */}
          {message.trim().length === 0 ? (
            <Button
              variant="outline"
              size="icon"
              onClick={handleMicClick}
              disabled={isProcessingAudio}
              className={cn(
                "shrink-0",
                isRecording &&
                  "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/20",
              )}
              aria-label={isRecording ? "Stop recording" : "Start recording"}
            >
              {isProcessingAudio ? (
                <Loader2 className="size-5 animate-spin text-slate-400" />
              ) : isRecording ? (
                <Square className="size-4 fill-red-600 text-red-600 dark:fill-red-400 dark:text-red-400" />
              ) : (
                <Mic className="size-5 text-slate-600 dark:text-slate-400" />
              )}
            </Button>
          ) : (
            <Button
              size="icon"
              onClick={handleSend}
              className="shrink-0"
              aria-label="Send message"
              disabled={isDisabled}
            >
              <Send className="size-5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
